import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface RejectLorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (feedback: string) => Promise<void>;
}

export const RejectLorModal: React.FC<RejectLorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [feedback, setFeedback] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const canSubmit = feedback.trim().length > 0;

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setFeedback('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onConfirm(feedback.trim());
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Handle Ctrl/Cmd+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      (e.metaKey || e.ctrlKey) &&
      canSubmit &&
      !isSubmitting
    ) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Reject Letter of Representation</DialogTitle>
          <DialogDescription>
            Provide feedback for the paralegal explaining why this LOR needs to
            be revised.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reject-feedback">
              Feedback <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reject-feedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what needs to be corrected or improved..."
              className="min-h-[120px] resize-none"
              disabled={isSubmitting}
              autoFocus
            />
            <p className="text-xs text-zinc-500">
              This feedback will be added as a note on the task for the assigned
              paralegal to review.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Rejecting…' : 'Reject LOR'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
